import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Location, matchPath, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    BULK_UPLOAD_CERTS_KEY,
    BULK_UPLOAD_PICTURES_KEY,
    DRAFT_BULK_UPLOAD_KEY,
    useDraftBulkUpload,
    useDraftBulkUploadDocuments,
    useFeatureFlag,
} from '@steelbuy/data-access';
import { createDateFromMaybeTimestamp, Feature } from '@steelbuy/ts-shared';
import { useBlocker } from '@steelbuy/ui-domain';
import {
    ButtonPrimary,
    LoadingSpinner,
    LoadingStatus,
    useWizardStepsContext,
    Wizard,
    WizardBar,
    WizardBarItemStatus,
} from '@steelbuy/ui-primitive';
import { useBulkListingContext } from './bulk-listing-context/BulkListingContext';
import { BulkListingStep } from './BulkListing.enums';
import { BULK_LISTING_STEPS, getIncompleteListingsCount, mapDraftToFormValues } from './bulkListingUtil';
import { RoutePath } from '../../router/Routes';
import { TableLayout } from '../../views/layouts/table-layout/TableLayout';
import { CreateUnsavedChangesModal } from '../common-utils/CreateUnsavedChangesModal';
import { PageHeader } from '../page-header/PageHeader';

const DIRTY_FIELDS = new Set(['listings', 'expireDate', 'autoRenew']);

const allowedPaths = [
    RoutePath.CREATE_BULK_LISTING_EDIT_LISTING_DRAFT,
    RoutePath.CREATE_BULK_LISTING_EXPIRY_DATE_DRAFT,
    RoutePath.CREATE_BULK_LISTING_SUMMARY_DRAFT,
    RoutePath.CREATE_BULK_LISTING_SUCCESS,
];

const allowNavigation = (newLocation: Location) => allowedPaths.some((path) => matchPath(path, newLocation.pathname));

export const BulkListing = () => {
    const { t } = useTranslation('translation');
    const queryClient = useQueryClient();
    const { isFeatureSupported } = useFeatureFlag();
    const navigate = useNavigate();
    const { pathname, state } = useLocation();
    const { stepStatus, setActiveStep, updateStepStatus } = useWizardStepsContext();
    const { getValues, reset, formState } = useFormContext();
    const { draftId = '' } = useParams();
    const [error, setError] = useState<boolean>(false);
    const { handleSaveDraft, updateBulkUploadMutation, draftSavedSuccessCallback, resetUpdatedIdsRef } =
        useBulkListingContext();
    const unblockOnSaveDraft = useRef<boolean>(false);
    const {
        data: draftBulkUpload,
        isLoading: isDraftLoading,
        isSuccess: isDraftSuccess,
        isError,
    } = useDraftBulkUpload(draftId);
    const { isLoading: isDocumentsLoading, isSuccess: isDocumentsSuccess } = useDraftBulkUploadDocuments({
        bulkId: draftId,
    });

    const createListing = isFeatureSupported(Feature.SINGLE_LISTING) ? t('application.createBulkListing.goBack') : '';

    const goBackLink = state?.goBackRoute ? t('application.myListings.drafts') : createListing;

    const isLoading = isDraftLoading || isDocumentsLoading;
    const isSuccess = isDraftSuccess && isDocumentsSuccess;

    const [modal, unblockAndRetry] = useBlocker(
        Object.keys(formState.dirtyFields).some((field) => DIRTY_FIELDS.has(field)),
        allowNavigation
    );

    let currentStep: number | null = 1;
    if (pathname.includes('/step')) {
        currentStep = Number.parseInt(pathname.split('/step')[1], 10);
    } else if (pathname.includes('/summary')) {
        currentStep = 0;
    }
    if (Number.isNaN(currentStep)) {
        currentStep = 1;
    }

    const currentStepIndex = currentStep - 1;

    useEffect(() => {
        draftSavedSuccessCallback(() => {
            if (unblockOnSaveDraft.current) {
                unblockAndRetry();
                reset();
                modal.hide();
            }
        });
    }, [unblockOnSaveDraft.current]);

    useEffect(() => {
        if (currentStep !== 0) {
            if (currentStep !== 1 && !getValues('listings') && !draftId) {
                return;
            }
            setActiveStep(BULK_LISTING_STEPS[currentStep - 1]);
        }
    }, [currentStep]);

    useEffect(
        () => () => {
            queryClient.removeQueries({ queryKey: [DRAFT_BULK_UPLOAD_KEY] });
            queryClient.removeQueries({ queryKey: [BULK_UPLOAD_CERTS_KEY] });
            queryClient.removeQueries({ queryKey: [BULK_UPLOAD_PICTURES_KEY] });
        },
        []
    );

    useEffect(() => {
        if (!draftBulkUpload) {
            return;
        }
        updateStepStatus(BulkListingStep.CHOOSE_MATERIAL, WizardBarItemStatus.SUCCESS);
        updateStepStatus(BulkListingStep.IMPORT_LISTINGS, WizardBarItemStatus.SUCCESS);
        reset({
            materialType: draftBulkUpload.materialType,
            product: draftBulkUpload.product,
            shape: draftBulkUpload.shape,
            prime: draftBulkUpload.prime,
            listings: mapDraftToFormValues(draftBulkUpload),
            autoRenew: draftBulkUpload.autoRenew,
            expireDate: createDateFromMaybeTimestamp(draftBulkUpload.expireDate).getOrUndefined(),
        });
    }, [isSuccess, draftBulkUpload, reset]);

    useEffect(() => {
        if (isError) {
            navigate(RoutePath.CREATE_LISTING_PORTAL);
        }
    }, [isError]);

    if (isLoading && draftId) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <PageHeader
                pageTitle={
                    currentStep === 0
                        ? t('application.createListingSummary.header')
                        : t('application.createBulkListing.header')
                }
                onBackClick={() => {
                    navigate(state?.goBackRoute || RoutePath.CREATE_LISTING_PORTAL);
                }}
                previousPageTitle={currentStep !== 0 ? goBackLink : ''}
            />

            <div>
                <Wizard>
                    <TableLayout>
                        <WizardBar
                            currentStepIndex={currentStepIndex}
                            items={BULK_LISTING_STEPS.map((step, index) => ({
                                label: t(`application.createBulkListing.labels.${step}`),
                                status: stepStatus[step],
                                selected: index === currentStepIndex,
                                onClick: undefined,
                                // () => {
                                // if (index !== currentStepIndex) {
                                //     navigate(getStepRoute(index + 1), { replace: true });
                                // }
                                // },
                            }))}
                        />
                        <Outlet
                            context={{
                                listingsIncompleteCount: getIncompleteListingsCount(getValues('listings') ?? []),
                                expireDate: getValues('expireDate'),
                            }}
                        />
                        <CreateUnsavedChangesModal
                            headline={t('application.createBulkListing.editListings.unsavedChanges')}
                            error={error}
                            modal={modal}
                            setError={setError}
                            unblockAndRetry={() => {
                                reset();
                                unblockAndRetry();
                                modal.hide();
                                resetUpdatedIdsRef();
                            }}
                        >
                            <ButtonPrimary
                                label={t('application.createListingSaveDraftButton.label')}
                                onClick={() => {
                                    unblockOnSaveDraft.current = true;
                                    handleSaveDraft(draftId);
                                }}
                                loadingStatus={
                                    updateBulkUploadMutation.isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE
                                }
                            />
                        </CreateUnsavedChangesModal>
                    </TableLayout>
                </Wizard>
            </div>
        </>
    );
};
